import { getType } from "mime";

export type FileType = "text" | "binary" | "image" | "safe_image" | "directory";

export interface FileInfo {
  type: FileType;
  language?: string;
  mimeType?: string;
  isPreviewable: boolean;
}

// File extension to language mapping for Monaco Editor
const LANGUAGE_MAP: Record<string, string> = {
  // Python
  py: "python",
  pyx: "python",
  pyi: "python",

  // YAML
  yaml: "yaml",
  yml: "yaml",

  // Text files
  txt: "plaintext",
  text: "plaintext",
  log: "plaintext",
  requirements: "plaintext",
};

// Binary file extensions - only for displayable images
const BINARY_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

// Safe image file extensions (can be safely displayed in browser)
const SAFE_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

// All image file extensions (same as safe since we only care about displayable ones)
const ALL_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.toLowerCase().split(".");
  if (parts.length <= 1) return "";

  // Handle special cases like .tar.gz
  if (parts.length >= 3 && parts[parts.length - 2] === "tar") {
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  }

  return parts[parts.length - 1];
}

/**
 * Detect if a filename represents a directory
 */
export function isDirectory(filename: string): boolean {
  return filename.endsWith("/");
}

/**
 * Get language for Monaco Editor based on file extension
 */
export function getLanguageFromFilename(filename: string): string | undefined {
  const ext = getFileExtension(filename);
  return LANGUAGE_MAP[ext];
}

/**
 * Check if a file is binary based on extension
 */
export function isBinaryFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return BINARY_EXTENSIONS.has(ext);
}

/**
 * Check if a file is a safe image that can be displayed
 */
export function isSafeImageFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return SAFE_IMAGE_EXTENSIONS.has(ext);
}

/**
 * Check if a file is any kind of image (including potentially unsafe ones)
 */
export function isImageFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return ALL_IMAGE_EXTENSIONS.has(ext);
}

/**
 * Check if a file can be previewed in the editor
 */
export function isPreviewableFile(filename: string): boolean {
  return (
    (!isBinaryFile(filename) && !isDirectory(filename)) ||
    isSafeImageFile(filename)
  );
}

/**
 * Get comprehensive file information
 */
export function getFileInfo(filename: string): FileInfo {
  if (isDirectory(filename)) {
    return {
      type: "directory",
      isPreviewable: false,
    };
  }

  if (isSafeImageFile(filename)) {
    return {
      type: "safe_image" as const,
      isPreviewable: true,
      mimeType: getType(filename) || undefined,
    };
  }

  if (isImageFile(filename)) {
    return {
      type: "image" as const,
      isPreviewable: false,
      mimeType: getType(filename) || undefined,
    };
  }

  if (isBinaryFile(filename)) {
    return {
      type: "binary",
      isPreviewable: false,
    };
  }

  return {
    type: "text",
    language: getLanguageFromFilename(filename),
    isPreviewable: true,
  };
}

/**
 * Fetches the Content-Type header from a URL using Replicate's content-type proxy.
 * Returns null if the request fails or no Content-Type header is present.
 * Used as a fallback when mime.getType() cannot determine the type from the URL
 * (e.g. URLs with query strings, or extensionless URLs).
 *
 * Uses a proxy endpoint to avoid CORS issues with external CDNs that don't
 * set Access-Control-Allow-Origin headers.
 */
export async function getMimeTypeFromUrl(
  url: string,
  signal?: AbortSignal
): Promise<string | null> {
  try {
    // Only proxy real remote URLs. blob:/data:/free-text values can't be
    // resolved by the proxy and would generate pointless requests.
    const { protocol } = new URL(url);
    if (protocol !== "http:" && protocol !== "https:") {
      return null;
    }

    const proxyUrl = `https://api.replicate.com/v1/utils/content-type?url=${encodeURIComponent(
      url
    )}`;
    const response = await fetch(proxyUrl, { signal });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const contentType = data?.["content-type"];
    return typeof contentType === "string" ? contentType : null;
  } catch (_error) {
    // Fetch failed (network error, invalid URL, aborted, etc.)
    return null;
  }
}

/**
 * Get a user-friendly description of the file type
 */
export function getFileTypeDescription(filename: string): string {
  const info = getFileInfo(filename);
  const ext = getFileExtension(filename);

  switch (info.type) {
    case "directory":
      return "Directory";
    case "safe_image":
      return `${ext.toUpperCase()} image`;
    case "image":
      return `${ext.toUpperCase()} image`;
    case "binary":
      return "Binary file";
    case "text":
      if (info.language) {
        return `${info.language} file`;
      }
      return "Text file";
    default:
      return "Unknown file type";
  }
}
